import { useCallback, useMemo, useState } from 'react';
import { Copy, Check, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { TrainingService } from '@/services/TrainingService';

interface HostingApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  outputName: string;
  apiKey: string;
  baseUrl: string;
  onRotated?: (next: { apiKey: string; baseUrl: string }) => void;
}

export default function HostingApiKeyDialog({
  open,
  onOpenChange,
  jobId,
  outputName,
  apiKey,
  baseUrl,
  onRotated,
}: HostingApiKeyDialogProps) {
  const { toast } = useToast();
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const curlExample = useMemo(
    () => `curl ${baseUrl}/chat/completions \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '{"model":"${outputName}","messages":[{"role":"user","content":"Hello"}],"stream":true}'`,
    [apiKey, baseUrl, outputName],
  );

  const pythonExample = useMemo(
    () => `from openai import OpenAI

client = OpenAI(base_url="${baseUrl}", api_key="${apiKey}")
stream = client.chat.completions.create(
    model="${outputName}",
    messages=[{"role": "user", "content": "Hello"}],
    stream=True,
)
for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="")`,
    [apiKey, baseUrl, outputName],
  );

  const jsExample = useMemo(
    () => `import OpenAI from "openai";

const client = new OpenAI({ baseURL: "${baseUrl}", apiKey: "${apiKey}" });
const stream = await client.chat.completions.create({
  model: "${outputName}",
  messages: [{ role: "user", content: "Hello" }],
  stream: true,
});
for await (const chunk of stream) process.stdout.write(chunk.choices[0]?.delta?.content || "");`,
    [apiKey, baseUrl, outputName],
  );

  const copy = useCallback(
    async (label: string, text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(label);
        window.setTimeout(() => {
          setCopied((c) => (c === label ? null : c));
        }, 1500);
      } catch {
        toast({
          title: 'Copy failed',
          description: 'Select the text and copy manually.',
          variant: 'destructive',
        });
      }
    },
    [toast],
  );

  const rotate = useCallback(async () => {
    if (rotating) return;
    setRotating(true);
    try {
      const result = await TrainingService.issueApiKey(jobId);
      onRotated?.({ apiKey: result.api_key, baseUrl: result.base_url });
      toast({ title: 'Key rotated', description: 'Previous key is now invalid.' });
    } catch (e: any) {
      toast({
        title: 'Could not rotate key',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setRotating(false);
    }
  }, [jobId, onRotated, rotating, toast]);

  const hasPlaintext = !!apiKey;

  // Tight row: small label, monospace value, copy button on the right.
  // Used for API key and Base URL so they sit side-by-side.
  const Field = ({ label, value, copyKey, masked = false }: {
    label: string; value: string; copyKey: string; masked?: boolean;
  }) => (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {label}
        </span>
        {!masked ? (
          <button
            type="button"
            onClick={() => copy(copyKey, value)}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            {copied === copyKey ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied === copyKey ? 'Copied' : 'Copy'}
          </button>
        ) : null}
      </div>
      <div className="truncate rounded border bg-muted/40 px-2 py-1.5 font-mono text-[11px]">
        {masked ? (
          <span className="text-muted-foreground">cnxp_live_•••••• — rotate to reveal</span>
        ) : value}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-base">API key — {outputName}</DialogTitle>
          <DialogDescription className="text-xs">
            {hasPlaintext
              ? 'Copy now — key shown once. Rotate any time to invalidate a leak.'
              : 'Already hosted. We don\'t store plaintext — rotate to mint a new key.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="API key" value={apiKey} copyKey="key" masked={!hasPlaintext} />
          <Field label="Base URL" value={baseUrl} copyKey="url" />
        </div>

        {hasPlaintext ? (
          <Tabs defaultValue="python" className="w-full">
            <TabsList className="h-8">
              <TabsTrigger value="python" className="text-xs">Python</TabsTrigger>
              <TabsTrigger value="js" className="text-xs">JavaScript</TabsTrigger>
              <TabsTrigger value="curl" className="text-xs">cURL</TabsTrigger>
            </TabsList>

            {[
              { value: 'python', code: pythonExample, copyKey: 'py' },
              { value: 'js', code: jsExample, copyKey: 'js' },
              { value: 'curl', code: curlExample, copyKey: 'curl' },
            ].map(({ value, code, copyKey }) => (
              <TabsContent key={value} value={value} className="mt-2">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => copy(copyKey, code)}
                    className="absolute right-2 top-2 inline-flex items-center gap-1 rounded bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground transition hover:text-foreground"
                  >
                    {copied === copyKey ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </button>
                  <pre className="max-h-[220px] overflow-auto rounded border bg-muted/40 p-3 pr-10 font-mono text-[11px] leading-tight">
                    {code}
                  </pre>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void rotate()}
            disabled={rotating}
          >
            {rotating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Rotate
          </Button>
          <Button type="button" size="sm" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
